import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AccionesRol,
  IModeloDispositivo,
  IFilter,
  IQueryParam,
  IPopulate,
  TipoDispositivo,
  NivelDimerizacion,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { ModelosDispositivosService } from '../../../../auxiliares/servicios/http/modelos-dispositivos.service';
import { CrearEditarModeloDispositivoComponent } from '../crear-editar-modelo-dispositivo/crear-editar-modelo-dispositivo.component';
import { DetallesCodigosDispositivosComponent } from '../../codigos-dispositivos/detalles-codigos-dispositivos/detalles-codigos-dispositivos.component';
import { ActivatedRoute } from '@angular/router';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ModeloDispositivoSelectorCacheService } from '../../../../componentes/selectores/modelo-dispositivo-selector/modelo-dispositivo-selector-cache.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import { estadoHtml } from '../../../../auxiliares/tabla/celdas';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-modelos-dispositivos',
  templateUrl: './listado-modelos-dispositivos.component.html',
  styleUrl: './listado-modelos-dispositivos.component.scss',
})
export class ListadoModelosDispositivosComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(ModelosDispositivosService);
  private formularios = inject(FormularioDialogService);
  private route = inject(ActivatedRoute);
  // El selector de modelos cachea la lista 5 min; sin invalidar, un formato
  // recién cargado no aparece en el alta de alarmas hasta que vence el TTL.
  private selectorCache = inject(ModeloDispositivoSelectorCacheService);

  public tipo: TipoDispositivo;
  //// Tabla nueva
  public name = 'ListadoModelosDispositivosComponent';
  public modelosDispositivos?: IModeloDispositivo[];
  public totalCount = 0;
  public columnas?: IColumnas<IModeloDispositivo>[];
  //Subs
  public modelosDispositivos$?: Subscription;
  //
  //

  //Permisos
  private puedeCrear: AccionesRol;
  private puedeEditar: AccionesRol;
  private puedeEliminar: AccionesRol;

  public botonCrear: ICrearBoton;
  public search: IRegExpSearch = {
    fields: ['marca', 'modelo'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];

  //Query
  public filter: IFilter<IModeloDispositivo> = {};
  public populate: IPopulate[] = [
    {
      path: 'codigos',
    },
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'marca modelo',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: false,
    childrenLevel: 0,
  };

  // Listados
  public async listar(): Promise<void> {
    const filter: IFilter<IModeloDispositivo> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    filter.tipo = this.tipo;
    this.query.filter = JSON.stringify(filter);

    //
    this.modelosDispositivos$?.unsubscribe();
    this.modelosDispositivos$ = this.lista2
      .modeloDispositivos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.modelosDispositivos = data.datos;
      });
  }

  // El formulario de luminaria trae curvas y tablas; el resto es un alta corta
  private get tamanoFormulario(): 'grande' | 'mediano' {
    return this.tipo === 'Luminaria' ? 'grande' : 'mediano';
  }

  // Acciones
  public async crear() {
    const dialogRef = this.formularios.abrir(
      CrearEditarModeloDispositivoComponent,
      {
        tamano: this.tamanoFormulario,
        data: {
          tipo: this.tipo,
        },
      },
    );
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.selectorCache.invalidate(this.tipo);
      this.listar();
    }
  }

  public async editar(modeloAlarma: IModeloDispositivo) {
    const dialogRef = this.formularios.abrir(
      CrearEditarModeloDispositivoComponent,
      {
        tamano: this.tamanoFormulario,
        data: {
          edit: modeloAlarma,
          tipo: this.tipo,
        },
      },
    );
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.selectorCache.invalidate(this.tipo);
      this.listar();
    }
  }

  public async codigos(codigosAlarma: IModeloDispositivo) {
    this.formularios.abrir(DetallesCodigosDispositivosComponent, {
      tamano: 'mediano',
      data: codigosAlarma?.codigos,
      cerrarAfuera: true,
    });
  }

  public async eliminar(modeloAlarma: IModeloDispositivo) {
    const res = await this.dialogService.confirm(
      'Confirmá la acción',
      `¿Querés eliminar el modelo ${modeloAlarma.marca} | ${modeloAlarma.modelo}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(modeloAlarma._id!);
      this.helper.notifSuccess('Eliminado correctamente');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private readonly NIVELES: NivelDimerizacion[] = [
    'dim10',
    'dim20',
    'dim30',
    'dim40',
    'dim50',
    'dim60',
    'dim70',
    'dim80',
    'dim90',
    'dim100',
  ];
  private readonly PCTS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

  /** Dato con su ícono (docs/ICONOS.md); sin ícono, sólo el texto. */
  private linea(icon: string | null, contenido: string, secundario = false): string {
    const clase = secundario ? 'tabla-linea tabla-secundario' : 'tabla-linea';
    const icono = icon
      ? `<span class="material-symbols-outlined">${icon}</span>`
      : '';
    return `<div class="${clase}">${icono}<span>${contenido}</span></div>`;
  }

  private badgeGlobal(): string {
    return estadoHtml({ texto: 'Global', tono: 'info', icono: 'public' });
  }

  private htmlModelo(dato: IModeloDispositivo): string {
    const marca = dato.marca
      ? `<span class="tabla-principal">${escaparHtml(dato.marca)}</span>`
      : `<span class="no-info">Sin marca</span>`;
    const modelo = dato.modelo ?? '';
    let html = `<div class="tabla-celda-fila">`;
    html += `<span class="material-symbols-outlined tabla-icono-dato">memory</span>`;
    html += `<div class="tabla-celda-col">`;
    html += `<div class="tabla-celda-fila">${marca}${dato.global ? this.badgeGlobal() : ''}</div>`;
    if (modelo) {
      html += `<span class="tabla-secundario">${escaparHtml(modelo)}</span>`;
    }
    html += `</div></div>`;
    return html;
  }

  private htmlCodigos(dato: IModeloDispositivo): string | null {
    const c = dato.codigos;
    if (!c?.nombre) return null;
    const count = c.codigos?.length ?? 0;
    let html = `<div class="tabla-celda-fila">`;
    html += `<span class="material-symbols-outlined tabla-icono-dato">integration_instructions</span>`;
    html += `<div class="tabla-celda-col">`;
    html += `<div class="tabla-celda-fila"><span class="tabla-principal">${escaparHtml(c.nombre)}</span>${c.global ? this.badgeGlobal() : ''}</div>`;
    if (count) {
      html += `<span class="tabla-secundario">${count.toLocaleString('es-AR')} código${count === 1 ? '' : 's'}</span>`;
    }
    html += `</div></div>`;
    return html;
  }

  private tooltipCodigos(dato: IModeloDispositivo): string | undefined {
    const codigos = dato.codigos?.codigos;
    if (!codigos?.length) return undefined;
    const items = codigos
      .slice(0, 12)
      .map(
        (c) =>
          `<div><strong>${escaparHtml(c.codigo ?? 'Sin código')}</strong>${c.descripcion ? ' · ' + escaparHtml(c.descripcion) : ''}</div>`,
      )
      .join('');
    const extra =
      codigos.length > 12
        ? `<div>…y ${(codigos.length - 12).toLocaleString('es-AR')} más</div>`
        : '';
    return items + extra;
  }

  private htmlElectrico(dato: IModeloDispositivo): string | null {
    const l = dato.luminarias;
    const v = l?.voltaje;
    const tieneHoras = l?.horasVida != null;
    const tieneVoltaje = v?.nominalV != null;
    if (!tieneHoras && !tieneVoltaje) return null;

    let html = `<div class="tabla-celda-col">`;
    if (tieneHoras) {
      // Duración: timer (docs/ICONOS.md)
      html += this.linea(
        'timer',
        `${l!.horasVida!.toLocaleString('es-AR')} h de vida`,
      );
    }
    if (tieneVoltaje) {
      let txt = `<strong>${v!.nominalV} V</strong>`;
      if (v!.margenSuperiorV != null || v!.margenInferiorV != null) {
        txt += ` <span class="tabla-secundario">(+${v!.margenSuperiorV ?? 0} / −${v!.margenInferiorV ?? 0})</span>`;
      }
      html += this.linea('bolt', txt);
    }
    html += `</div>`;
    return html;
  }

  private htmlCurva(dato: IModeloDispositivo): string | null {
    const dim = dato.luminarias?.dimerizacion;
    if (!dim) return null;

    const pots = this.NIVELES.map((n) => dim[n]?.potencia).filter(
      (x): x is number => x != null,
    );
    const fps = this.NIVELES.map((n) => dim[n]?.factorPotencia).filter(
      (x): x is number => x != null,
    );
    const nDef = this.NIVELES.filter(
      (n) => dim[n] && Object.keys(dim[n]!).length > 0,
    ).length;
    if (!pots.length && !fps.length) return null;

    let html = `<div class="tabla-celda-col">`;
    if (pots.length) {
      const min = Math.min(...pots);
      const max = Math.max(...pots);
      const p100 = dim.dim100?.potencia ?? max;
      const rango =
        min !== max
          ? ` <span class="tabla-secundario">(${min}–${max} W)</span>`
          : '';
      // Potencia: bolt (docs/ICONOS.md)
      html += this.linea('bolt', `<strong>${p100} W</strong>${rango}`);
    }
    if (fps.length) {
      const fp100 = dim.dim100?.factorPotencia ?? fps[fps.length - 1];
      // Factor de potencia: sin ícono en la guía, sólo texto
      html += this.linea(null, `FP <strong>${fp100}</strong>`);
    }
    html += this.linea('brightness_5', `${nDef}/10 niveles`, true);
    html += `</div>`;
    return html;
  }

  private tooltipCurva(dato: IModeloDispositivo): string | undefined {
    const dim = dato.luminarias?.dimerizacion;
    if (!dim) return undefined;
    let rows = '';
    this.NIVELES.forEach((n, i) => {
      const p = dim[n];
      if (!p || Object.keys(p).length === 0) return;
      const pot =
        p.potencia != null
          ? `${p.potencia.toLocaleString('es-AR')} W`
          : 'Sin dato';
      const fp =
        p.factorPotencia != null
          ? p.factorPotencia.toLocaleString('es-AR')
          : 'Sin dato';
      rows += `<div>${this.PCTS[i]} %: <strong>${pot}</strong> · FP <strong>${fp}</strong></div>`;
    });
    return rows || undefined;
  }

  private get esConCodigos(): boolean {
    return (
      this.tipo !== 'Luminaria' && this.tipo !== 'Cámara' && this.tipo !== 'NVR'
    );
  }

  private setColumnas() {
    const columnas: IColumnas<IModeloDispositivo>[] = [
      // Modelo (marca + modelo + global)
      {
        header: {
          label: 'Modelo',
          sort: 'marca',
          sortable: true,
        },
        row: {
          html: (dato) => this.htmlModelo(dato),
        },
      },
    ];

    // También para Alarma: un panel con comunicación integrada define acá su
    // formato y hay que poder verificarlo sin abrir el modelo.
    if (this.tipo === 'Comunicador' || this.tipo === 'Alarma') {
      columnas.push({
        header: {
          label: 'Formato del mensaje',
        },
        row: {
          // Sin ícono en docs/ICONOS.md para el formato: sólo el texto
          parse: (dato) => dato.formatoMensaje,
          noData: 'Sin formato',
        },
      });
    }

    if (this.esConCodigos) {
      columnas.push({
        header: {
          label: 'Códigos',
        },
        row: {
          html: (dato) => this.htmlCodigos(dato),
          tooltip: (dato) => this.tooltipCodigos(dato),
          noData: 'Sin códigos',
        },
      });
    }

    if (this.tipo === 'Luminaria') {
      columnas.push({
        header: {
          label: 'Configuración eléctrica',
        },
        row: {
          html: (dato) => this.htmlElectrico(dato),
          noData: 'Sin configuración',
        },
      });
      columnas.push({
        header: {
          label: 'Curva de dimerización',
        },
        row: {
          html: (dato) => this.htmlCurva(dato),
          tooltip: (dato) => this.tooltipCurva(dato),
          noData: 'Sin curva',
        },
      });
    }

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) =>
            dato.cliente?.nombre
              ? this.linea('business', escaparHtml(dato.cliente.nombre))
              : null,
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          // Que sea global se ve como estado en la columna Modelo
          noData: 'Sin cliente',
        },
      });
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        accionesGrouped: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(this.puedeEditar) ||
              (dato.global && !this.helper.esNivel0()),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'visibility',
            tooltip: 'Ver códigos',
            click: (dato) => this.codigos(dato),
            oculta: () => !this.esConCodigos,
            disabled: () => !this.esConCodigos,
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: (dato) =>
              !this.helper.tienePermiso(this.puedeEliminar) ||
              (dato.global && !this.helper.esNivel0()),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  private setPermisos(tipo: TipoDispositivo): void {
    switch (tipo) {
      case 'Tracker':
        this.puedeCrear = 'Configuraciones - Crear modelos trackers';
        this.puedeEditar = 'Configuraciones - Editar modelos trackers';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos trackers';
        break;
      case 'Dashcam':
        this.puedeCrear = 'Configuraciones - Crear modelos dashcams';
        this.puedeEditar = 'Configuraciones - Editar modelos dashcams';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos dashcams';
        break;
      case 'Alarma':
        this.puedeCrear = 'Configuraciones - Crear modelos alarmas';
        this.puedeEditar = 'Configuraciones - Editar modelos alarmas';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos alarmas';
        break;
      case 'Comunicador':
        this.puedeCrear = 'Configuraciones - Crear modelos comunicadores';
        this.puedeEditar = 'Configuraciones - Editar modelos comunicadores';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos comunicadores';
        break;
      case 'Cámara':
      case 'NVR':
        this.puedeCrear = 'Configuraciones - Crear modelos cámaras';
        this.puedeEditar = 'Configuraciones - Editar modelos cámaras';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos cámaras';
        break;
      case 'Luminaria':
        this.puedeCrear = 'Configuraciones - Crear modelos luminarias';
        this.puedeEditar = 'Configuraciones - Editar modelos luminarias';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos luminarias';
        break;
      case 'DispositivoLorawan':
        this.puedeCrear =
          'Configuraciones - Crear modelos dispositivos lorawan';
        this.puedeEditar =
          'Configuraciones - Editar modelos dispositivos lorawan';
        this.puedeEliminar =
          'Configuraciones - Eliminar modelos dispositivos lorawan';
        break;
      case 'BotonBLE':
        this.puedeCrear = 'Configuraciones - Crear modelos botones BLE';
        this.puedeEditar = 'Configuraciones - Editar modelos botones BLE';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos botones BLE';
        break;
      case 'Sirena':
        this.puedeCrear = 'Configuraciones - Crear modelos sirenas';
        this.puedeEditar = 'Configuraciones - Editar modelos sirenas';
        this.puedeEliminar = 'Configuraciones - Eliminar modelos sirenas';
        break;
    }
  }
  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.route.params.subscribe(async (params) => {
      this.tipo = params['tipo'] as TipoDispositivo;
      this.setPermisos(this.tipo);
      this.setColumnas();

      this.botonCrear = {
        mostrar: this.helper.tienePermiso(this.puedeCrear),
        tooltip: 'Crear modelo',
        color: 'primary',
        tipo: 'material',
        icon: 'add',
        accion: () => this.crear(),
      };
      await this.listar();
    });
  }

  async ngOnDestroy() {
    this.modelosDispositivos$?.unsubscribe();
  }
}
